<template>
    <div>

        <b-breadcrumb>
            <b-breadcrumb-item to="/">
                <i class="fa fa-home"></i>
                Home
            </b-breadcrumb-item>
            <b-breadcrumb-item to="/Facilities/Keys">Facilities</b-breadcrumb-item>
            <b-breadcrumb-item to="/Facilities/Keys">Keys</b-breadcrumb-item>
            <b-breadcrumb-item :to="{name:'KeyDetails',params:{id:facilitiesKeyId,keyNumber:facilitiesKeyNumber}}">{{facilitiesKeyNumber}}</b-breadcrumb-item>
            <b-breadcrumb-item active>Add Copies</b-breadcrumb-item>
        </b-breadcrumb>

        <form @submit.prevent="onSubmit()">
            <div class="card">
                <div class="bg-dark card-header text-white">Create New Copies</div>
                <div class="card-body">
                    <div class="pb-3">
                      <b>Key Type:</b> {{ facilitiesKeyTypeName }}
                    </div>
                    <AppAlertNote>
                        Changing the range after setting will reset the list of key copies in the form
                    </AppAlertNote>
                    <AppAlertNote v-if="lastCopyNumber !== null">
                        The last copy number was {{ lastCopyNumber }}
                    </AppAlertNote>
                    <div class="row">
                        <b-form-group class="col-lg-6 col-md-12" label="Format" v-slot="{ formatValue }">
                            <b-form-radio-group
                              v-model="format"
                              :options="formatOptions"
                              :aria-describedby="formatValue"
                              @change="clearValues()"
                              :disabled="!rangeEnabled"
                            ></b-form-radio-group>
                        </b-form-group>
                    </div>
                    <div class="row">
                        <div class="col-lg-2 col-md-3 form-group">
                            <label for="startLetter">Starting Letter</label>
                            <input :class="{'is-invalid': $v.startLetter.$invalid && isValidating}"
                                    class="form-control"
                                    maxlength="1"
                                    v-model="startLetter"
                                    :disabled="!rangeEnabled"
                                    @input="startLetter = $event.target.value.toUpperCase();createLetterRange();"/>
                            <span class="invalid-feedback" v-if="!$v.startLetter.required && isValidating">Field is required</span>
                            <span class="invalid-feedback" v-if="!$v.startLetter.alpha && isValidating">Field cannot include anything other than letters</span>
                        </div>
                        <div class="col-lg-2 col-md-3 form-group">
                            <label for="endLetter">Ending Letter</label>
                            <input :class="{'is-invalid': $v.endLetter.$invalid && isValidating}"
                                    class="form-control"
                                    maxlength="1"
                                    v-model="endLetter"
                                    :disabled="!rangeEnabled"
                                    @input="endLetter = $event.target.value.toUpperCase();createLetterRange();"/>
                            <span class="invalid-feedback" v-if="!$v.endLetter.required && isValidating">Field is required</span>
                            <span class="invalid-feedback" v-if="!$v.endLetter.alpha && isValidating">Field cannot include anything other than letters</span>
                        </div>
                    </div>
                    <div v-if="format !== 1" class="row">
                        <div class="col-lg-2 col-md-3 form-group">
                            <label for="startNumber">Starting Number</label>
                            <input :class="{'is-invalid': $v.startNumber.$invalid && isValidating}"
                                    class="form-control"
                                    v-model="startNumber"
                                    :disabled="!rangeEnabled"
                                    @input="createNumberRange()" />
                            <span class="invalid-feedback" v-if="!$v.startNumber.required && isValidating">Field is required</span>
                            <span class="invalid-feedback" v-if="!$v.startNumber.alpha && isValidating">Field cannot include anything other than numbers</span>
                        </div>
                        <div class="col-lg-2 col-md-3 form-group">
                            <label for="endNumber">Ending Number</label>
                            <input :class="{'is-invalid': $v.endNumber.$invalid && isValidating}"
                                    class="form-control"
                                    v-model="endNumber"
                                    :disabled="!rangeEnabled"
                                    @input="createNumberRange()" />
                            <span class="invalid-feedback" v-if="!$v.endNumber.required && isValidating">Field is required</span>
                            <span class="invalid-feedback" v-if="!$v.endNumber.alpha && isValidating">Field cannot include anything other than numbers</span>
                        </div>
                    </div>
                    <div v-if="showRangeEnabledButton" class="mb-2 text-left">
                        <button @click.prevent="toggleRangeFields()" class="btn btn-primary d-print-none">
                            {{ rangeEnabled ? 'Set Range' : 'Change Range' }}
                        </button>
                    </div>
                    <BTable v-if="!rangeEnabled && newFacilitiesKeyCopies.length > 0"
                            :fields="tableFields"
                            :items="newFacilitiesKeyCopies"
                            :responsive="true"
                            primary-key="copyNumber"
                            show-empty
                            striped
                            table-class="table-align-middle">

                        <template v-slot:cell(copyNumber)="row">
                            <input :class="{'is-invalid': $v.newFacilitiesKeyCopies.$each[row.index].copyNumber.$invalid && isValidating}"
                                   class="form-control"
                                   maxlength="3"
                                   v-model="row.item.copyNumber"
                                   @input="row.item.copyNumber = $event.target.value.toUpperCase()"/>
                            <span class="invalid-feedback" v-if="!$v.newFacilitiesKeyCopies.$each[row.index].copyNumber.required && isValidating">Field is required</span>
                            <span class="invalid-feedback" v-if="!$v.newFacilitiesKeyCopies.$each[row.index].copyNumber.letterNumberFormat && isValidating && format !== 1">The field must be in the [Capital-Letter][Number] format</span>
                            <span class="invalid-feedback" v-if="!$v.newFacilitiesKeyCopies.$each[row.index].copyNumber.letterNumber && isValidating && format !== 0">The field must be in the [Capital-Letter] format</span>
                        </template>
                        <template v-slot:cell(facilitiesKeyCopyStatusId)="row">
                            <select :class="{'is-invalid': $v.newFacilitiesKeyCopies.$each[row.index].facilitiesKeyCopyStatusId.$invalid && isValidating}"
                                    class="form-control" v-model="row.item.facilitiesKeyCopyStatusId"
                                    @change="updateRowValues(row.index)">
                                <option :value="null" disabled>--Select Status--</option>
                                <option :key="facilitiesKeyCopyStatus.id" :value="facilitiesKeyCopyStatus.id" v-for="facilitiesKeyCopyStatus in facilitiesKeyCopyStatusList">{{facilitiesKeyCopyStatus.name}}</option>
                            </select>
                            <span class="invalid-feedback" v-if="!$v.newFacilitiesKeyCopies.$each[row.index].facilitiesKeyCopyStatusId.required && isValidating">Field is required</span>
                        </template>
                        <template v-slot:cell(hookNumber)="row">
                            <input :class="{'is-invalid': $v.newFacilitiesKeyCopies.$each[row.index].hookNumber.$invalid && isValidating}"
                                   :disabled="row.item.facilitiesKeyCopyStatusId !== facilitiesKeyCopyStatuses.Stored" class="form-control"
                                   maxlength="3"
                                   v-model="row.item.hookNumber"/>
                            <span class="invalid-feedback" v-if="!$v.newFacilitiesKeyCopies.$each[row.index].hookNumber.required && isValidating">Field is required</span>
                        </template>
                        <template v-slot:cell(issuedToPersonId)="row">
                            <select :class="{'is-invalid': $v.newFacilitiesKeyCopies.$each[row.index].issuedToPersonId.$invalid && isValidating}"
                                    :disabled="row.item.facilitiesKeyCopyStatusId !== facilitiesKeyCopyStatuses.Issued" class="form-control" v-model="row.item.issuedToPersonId">
                                <option :value="null" disabled>--Select Person--</option>
                                <option :key="person.id" :value="person.id" v-for="person in people">{{person.name}}</option>
                            </select>
                            <span class="invalid-feedback" v-if="!$v.newFacilitiesKeyCopies.$each[row.index].issuedToPersonId.required && isValidating">Field is required</span>
                        </template>
                    </BTable>
                </div>
                <div class="card-footer d-print-none text-right">
                    <button :disabled="isSubmitting || rangeEnabled" class="btn btn-primary" type="submit">Save</button>
                    <RouterLink :to="{name:'KeyDetails',params:{id:facilitiesKeyId,keyNumber:facilitiesKeyNumber}}" class="btn btn-danger">Back</RouterLink>
                </div>
            </div>
        </form>

    </div>
</template>

<script>
    import AppAlertNote from "@/components/shared/AppAlertNote";
    import {ToastType} from "@/services/models/shared/ToastType";
    import {required, requiredIf, alpha, numeric, helpers} from "vuelidate/lib/validators";
    import FacilitiesKeysService from "@/services/FacilitiesKeysService";
    import PersonService from "@/services/PersonService";
    import { FacilitiesKeyCopyStatus } from "@/services/models/FacilitiesKey";

    const letterNumberFormat = helpers.regex('letterNumberFormat', /^([A-Z]+)([0-9]?)$/gm)
    const letterFormat = helpers.regex('letterFormat', /^([A-Z]+)$/gm)

    export default {
        components: {
          AppAlertNote
        },
        props: {
            facilitiesKeyId: {
              type: String,
              required: true
            },
            facilitiesKeyNumber: {
              type: String,
              required: true
            },
            facilitiesKeyTypeName: {
              type: String,
              required: true
            }
        },
        data() {
            return {
                isSubmitting: false,
                isValidating: false,
                rangeEnabled: true,
                lastCopyNumber: null,
                startLetter: null,
                endLetter: null,
                startNumber: null,
                endNumber: null,
                format: 0,
                formatOptions: [
                    {
                        value: 0,
                        text: '[Letter][Number]'
                    },
                    {
                        value: 1,
                        text: 'Letter only'
                    }
                ],
                tableFields: [
                    {
                        key: 'copyNumber',
                        label: 'Copy Number'
                    },
                    {
                        key: 'facilitiesKeyCopyStatusId',
                        label: 'Status',
                    },
                    {
                        key: 'hookNumber',
                        label: 'Hook Number',
                    },
                    {
                        key: 'issuedToPersonId',
                        label: 'Issue to'
                    }
                ],
                people: [],
                facilitiesKeyCopyStatuses: FacilitiesKeyCopyStatus,
                letterRange: [],
                numberRange: [],
                newFacilitiesKeyCopies: []
            }
        },
        computed: {
          facilitiesKeyCopyStatusList () {
              return (Object.keys(FacilitiesKeyCopyStatus)).map(val => {
                return {
                    id: FacilitiesKeyCopyStatus[val],
                    name: val.toString()
                }
            })
          },

          showRangeEnabledButton () {
            switch (this.format) {
              case 0:
                return this.letterRange.length > 0 && this.numberRange.length > 0 && this.endLetter !== null && this.endLetter !== '' && this.endNumber !== null;
              case 1: 
                return this.letterRange.length > 0 && this.endLetter !== null && this.endLetter !== '';
            }
            return false;
          }
        },
        methods: {
            loadLastKeyCopyNumber () {
                const vm = this;
                FacilitiesKeysService.getLastFacilitiesKeyCopyForKey(this.facilitiesKeyId)
                    .then(response => {
                      if (!response.copyNumber || response.copyNumber === undefined || response.copyNumber === null || response.copyNumber.trim() === '') {
                        this.lastCopyNumber = null;
                        return;
                      }

                      this.lastCopyNumber = response.copyNumber
                      const copyNumberParts = response.copyNumber.split(/^([A-Z]+)/gm)
                      let possibleStartNumber = 0;
                      let possibleStartLetter = '';
                      copyNumberParts.forEach((x) => {
                        if (!x || x === undefined || x === null || x.trim() === '') {
                          return;
                        } else if (/^\d+$/.test(x)) {
                          possibleStartNumber = x;
                        } else {
                          possibleStartLetter = x;
                        }
                      })

                      if (possibleStartLetter === 'Z') {
                        this.startLetter = 'A';
                        this.startNumber = parseInt(possibleStartNumber) + 1;
                      } else if (parseInt(possibleStartNumber) === 0) {
                        this.format = 1;
                        this.startLetter = String.fromCharCode(possibleStartLetter.charCodeAt(0) + 1);
                      } else {
                        this.startLetter = String.fromCharCode(possibleStartLetter.charCodeAt(0) + 1);
                        this.startNumber = parseInt(possibleStartNumber);
                      }
                    })
            },

            /**
             * @description Creates key copies and redirects if successful
             */
            onSubmit() {
                const vm = this;
                vm.isValidating = true;

                // stop here if form is invalid
                this.$v.$touch();
                if (this.$v.$invalid) {
                    return;
                }

                vm.isSubmitting = true;
                FacilitiesKeysService.addFacilitiesKeyCopies(this.facilitiesKeyId, this.newFacilitiesKeyCopies)
                    .then(() => {
                      vm.alert(ToastType.Success, "The key copies were successfully created");

                      // Redirect to the key detail page
                      vm.$router.replace({name:'KeyDetails',params:{id:vm.facilitiesKeyId,keyNumber:vm.facilitiesKeyNumber}})
                    })
                    .catch((error) => {
                      vm.alert(ToastType.Danger, error.response.data)
                    })
                    .finally(() => {
                      vm.isValidating = false;
                      vm.isSubmitting = false;
                    })
            },

            setRangeValues () {
              switch (this.format) {
                case 0:
                  this.numberRange.forEach(n => {
                    this.letterRange.forEach(l => {
                      this.newFacilitiesKeyCopies.push({
                        copyNumber: l + n.toString(),
                        hookNumber: null,
                        facilitiesKeyCopyStatusId: FacilitiesKeyCopyStatus.Stored,
                        createdByPersonId: this.$store.getters.user.id,
                        issuedToPersonId: null
                      });
                    })
                  })
                  break;
                case 1:
                  this.letterRange.forEach(l => {
                    this.newFacilitiesKeyCopies.push({
                      copyNumber: l,
                      hookNumber: null,
                      facilitiesKeyCopyStatusId: FacilitiesKeyCopyStatus.Stored,
                      createdByPersonId: this.$store.getters.user.id,
                      issuedToPersonId: null
                    });
                  })
                  break;
              }
            },

            clearValues () {
              this.startLetter = null;
              this.endLetter = null;
              this.startNumber = null;
              this.endNumber = null;
              this.letterRange = [];
              this.numberRange = [];
              this.newFacilitiesKeyCopies = [];
            },

            createLetterRange () {
              this.letterRange = [];
              if (this.startLetter !== null && this.startLetter !== '') {
                if (this.endLetter !== null && this.endLetter !== '') {
                  if (this.startLetter === this.endLetter) {
                    this.letterRange = [(this.startLetter ?? 'A')];
                    return;
                  } else if ((this.endLetter ?? 'A') > (this.startLetter ?? 'A')) {
                    const length = (this.endLetter ?? 'A').charCodeAt(0) - (this.startLetter ?? 'A').charCodeAt(0) + 1;
                    this.letterRange = ([...Array(length).keys()].map(i => { return String.fromCharCode(i + this.startLetter.charCodeAt(0)) })).filter(i => { return i !== 'I' && i !== 'O'; });
                    return;
                  }
                  this.letterRange = [];
                  return;
                }
                this.letterRange = [(this.startLetter ?? 'A')];
                return
              }
            },

            createNumberRange () {
              if (this.format !== 1) {
                this.numberRange = [];
                if (this.startNumber !== null) {
                  if (this.endNumber !== null) {
                    if (this.startNumber === this.endNumber) {
                      this.numberRange = [(this.startNumber ?? 0)];
                      return;
                    } else if (+(this.endNumber ?? 0) > +(this.startNumber ?? 0)) {
                      const length = (this.endNumber ?? 0) - (this.startNumber ?? 0) + 1
                      this.numberRange = [...Array(length).keys()].map(i => { return +i + +(this.startNumber ?? 0) });
                      return;
                    }
                    this.numberRange = [];
                    return;
                  }
                  this.numberRange = [(this.startNumber ?? 0)];
                  return
                }
              }
              this.numberRange = [];
            },

            toggleRangeFields () {
              if (this.rangeEnabled) {
                this.rangeEnabled = false;
                this.setRangeValues();
              } else {
                this.rangeEnabled = true;
                this.newFacilitiesKeyCopies = []
              }
            },

            updateRowValues (index) {
              if (this.newFacilitiesKeyCopies[index].facilitiesKeyCopyStatusId === FacilitiesKeyCopyStatus.Stored) {
                this.newFacilitiesKeyCopies[index].issuedToPersonId = null;
              } else if (this.newFacilitiesKeyCopies[index].facilitiesKeyCopyStatusId === FacilitiesKeyCopyStatus.Issued) {
                this.newFacilitiesKeyCopies[index].hookNumber = null
              } else {
                this.newFacilitiesKeyCopies[index].issuedToPersonId = null;
                this.newFacilitiesKeyCopies[index].hookNumber = null;
              }
            }
        },
        created() {
            const vm = this;

            PersonService.getAllPeople()
                .then(response => {
                    vm.people = response
                }).catch((error) => {
                    vm.alert(ToastType.Danger, error.response.data)
                    // Redirect to the key detail page
                    vm.$router.replace({name:'KeyDetails',params:{id:vm.facilitiesKeyId,keyNumber:vm.facilitiesKeyNumber}})
                })

            this.loadLastKeyCopyNumber()
        },
        validations: {
          startLetter: {
                required,
                alpha
            },
            endLetter: {
                required,
                alpha
            },
            startNumber: {
                required: requiredIf(function () {
                    return this.format === 0;
                }),
                numeric
            },
            endNumber: {
                required: requiredIf(function () {
                    return this.format === 0;
                }),
                numeric
            },
            newFacilitiesKeyCopies: {
              $each: {
                copyNumber: {
                  required,
                  formatCheck(value) {
                    return this.format === 0 ? (/^([A-Z]+)([0-9]?)$/gm).test(value) : (/^([A-Z]+)$/gm).test(value);
                  }
                },
                facilitiesKeyCopyStatusId: {
                  required
                },
                hookNumber: {
                  required: requiredIf(function (val) {
                    return val.facilitiesKeyCopyStatusId === this.facilitiesKeyCopyStatuses.Stored
                  })
                },
                issuedToPersonId: {
                  required: requiredIf(function (val) {
                    return val.facilitiesKeyCopyStatusId === this.facilitiesKeyCopyStatuses.Issued
                  })
                }
              }
            }
        }
    }
</script>
